import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useRoutes } from "../hooks/RoutesProvider/RoutesProvider";
import PrivateRoute from "../services/PrivateRoute";
import { useAuth } from "../hooks/AuthProvider/AuthProvider";

import TmcSolicitudes from "../page/TMCListPage";
import Withdrawal from "../page/WithdrawalPage";
import ProspectsPage from "../page/ProspectsPage";
import AdelantosEfectivoPage from "../components/AdelantosEfectivoParametros/AdelantosEfectivoPage";
import PrestamosParametrizadosPage from "../components/PrestamosParametrizados/PrestamosParametrizadosPage";

// Code splitting: cada página se carga solo cuando se navega a ella
const RolesPage = lazy(() => import("../page/RolesPage"));
const HomePage = lazy(() => import("../page/HomePage"));
const LoginPage = lazy(() => import("../page/LoginPage"));
const UserPage = lazy(() => import("../page/UserPage"));
const LicencePage = lazy(() => import("../page/LicencePage"));
const ManageLicencePage = lazy(() => import("../page/ManageLicencePage"));
const ProfilePage = lazy(() => import("../page/ProfilePage"));
const AssignedInventoryPage = lazy(
  () => import("../page/AssignedInventoryPage"),
);
const StockInventoryPage = lazy(() => import("../page/StockInventoryPage"));
const OrganizationMethodsPage = lazy(
  () => import("../page/OrganizationMethodsPage"),
);
const LicenceInfoPage = lazy(() => import("../page/LicenceInfoPage"));
const TicketPage = lazy(() => import("../page/TicketPage"));
const PowerbiPage = lazy(() => import("../page/PowerbiPage"));
const ReceiptPage = lazy(() => import("../page/ReceiptPage"));
const ManualsPage = lazy(() => import("../page/ManualsPage"));
const AdministrarManualesPage = lazy(() => import("../page/AdministrarManualesPage"));
const IngresosEmpleadosPublicosPage = lazy(() => import("../page/IngresosEmpleadosPublicosPage"));
const CalculadoraEmpleadosPublicosPage = lazy(() => import("../page/CalculadoraEmpleadosPublicosPage"));
const DirectoryPage = lazy(() => import("../page/DirectoryPage"));
const AsteriskPage = lazy(() => import("../page/AsteriskPage"));
const AuditsPage = lazy(() => import("../page/AuditsPage"));
const AppMobilePage = lazy(() => import("../page/AppMobilePage"));
const MarketingPage = lazy(() => import("../page/MarketingPage"));
const TMCSalePage = lazy(() => import("../page/TMCSalePage"));
const StatusesPage = lazy(() => import("../page/StatusesPage"));
const OnlinePaymentPage = lazy(() => import("../page/OnlinePaymentPage"));
const PaymentReceiptReportPage = lazy(
  () => import("../page/PaymentReceiptReportPage"),
);
const PaymentReportPage = lazy(() => import("../page/PaymentReportPage"));
const PaymentReconciliationPage = lazy(
  () => import("../page/PaymentReconciliationPage"),
);
const ResumenPage = lazy(() => import("../page/ResumenPage"));
const ComerceQr = lazy(() => import("../page/ComerceQrPage"));
const AdminProspectosPage = lazy(() => import("../page/AdminProspectosPage"));
const GestionAltasSocioPage = lazy(() => import("../page/GestionAltasSocioPage"));
const GestionPrestamosPage = lazy(() => import("../page/GestionPrestamosPage"));
const DashboardManagerPage = lazy(() => import("../page/DashboardManagerPage"));
const ValidationStatusPage = lazy(() => import("../page/ValidationStatusPage"));
const UpdateLoansPage = lazy(() => import("../page/UpdateLoansPage"));
const TurneroPage = lazy(() => import("../page/TurneroPage"));
const BannersPage = lazy(() => import("../page/BannersPage"));
const TasasComisionesPage = lazy(() => import("../page/TasasComisionesPage"));
const SubirTasasPDFPage = lazy(() => import("../page/SubirTasasPDFPage"));
const OxygenCancellationPage = lazy(
  () => import("../page/OxygenCancellationPage"),
);
const BindPage = lazy(() => import("../page/BindPage"));
const TurnosPage = lazy(() => import("../page/TurnosPage"));
const ComerciosPrimeraVentaPage = lazy(
  () => import("../page/ComerciosPrimeraVentaPage"),
);
const CampaniasPage = lazy(() => import("../page/CampaniasPage"));
const RespuestasAutomaticasPage = lazy(
  () => import("../page/RespuestasAutomaticasPage"),
);
const CaprescaPage = lazy(() => import("../page/CaprescaPage"));
const SolicitudesLimitesPage = lazy(
  () => import("../page/SolicitudesLimitesPage"),
);
const EstadisticasLimitesPage = lazy(
  () => import("../page/EstadisticasLimitesPage"),
);
const CaprescaOperadorPage = lazy(() => import("../page/CaprescaOperadorPage"));
const BnaPage = lazy(() => import("../page/BnaPage"));
const EventosSistemaPage = lazy(() => import("../page/EventosSistemaPage"));
const SystemsIncidentLogPage = lazy(() => import("../page/SystemsIncidentLogPage"));
const TenantsApiPage = lazy(() => import("../page/TenantsApiPage"));
const LotesPage = lazy(() => import("../page/LotesPage"));

const DynamicRoutes = () => {
  const routes = useRoutes();
  const isNewUser = localStorage.getItem("newUser") ? true : false;
  const routesLoading = routes.routesLoading;

  if (!isNewUser && routesLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Cargando...
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          Cargando...
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/Login" element={<LoginPage />} />

        <Route
          path="/solicitud-vacaciones"
          element={<ManageLicencePage type="vacations" />}
        />
        <Route
          path="/solicitud-licencia"
          element={<ManageLicencePage type="licences" />}
        />
        {/* Ruta temporal para pruebas del Turnero Comercial */}
        <Route path="/turnero" element={<TurneroPage />} />

        {/* Redirección para nuevos usuarios */}
        {isNewUser ? (
          <>
            <Route path="/mi-perfil" element={<ProfilePage />} />
            <Route path="*" element={<Navigate to="/mi-perfil" replace />} />
          </>
        ) : (
          <Route element={<PrivateRoute />}>
            {routes.menus.map((menu) => (
              <Route
                key={menu.id}
                path={menu.url}
                element={getComponentByName(menu.name)}
              />
            ))}
            {routes.menuItems.map((menuItem) => (
              <Route
                key={menuItem.id}
                path={menuItem.url}
                element={getComponentByName(menuItem.name, menuItem)}
              />
            ))}
            {routes.itemSubmenus.map((subMenu) => (
              <Route
                key={subMenu.id}
                path={subMenu.url}
                element={getComponentByName(subMenu.name, subMenu)}
              />
            ))}
          </Route>
        )}
        <Route path="*" element={<CatchAllRoute />} />
      </Routes>
    </Suspense>
  );
};

// Catch-all: espera a que termine la verificación de sesión antes de redirigir.
// Evita la condición de carrera donde routesLoading=false brevemente antes
// de que se carguen las rutas dinámicas tras el login.
const CatchAllRoute = () => {
  const { token, sessionLoading } = useAuth();
  const { routesLoading } = useRoutes();
  if (sessionLoading || routesLoading) return null;
  if (!token) return <Navigate to="/" replace />;
  return <Navigate to="/Home" replace />;
};

const getComponentByName = (name, routeItem = {}) => {
  if (routeItem.url?.startsWith("/dashboard/")) {
    return <PowerbiPage dashboardId={routeItem.id} />;
  }

  switch (routeItem.url) {
    case "/ingresar-venta-banka":
      return <TMCSalePage key="sale-banka" type="sale" theme="green" />;
    case "/venta-producto-stock":
      return <TMCSalePage key="sale-product" type="sale" theme="blue" />;
    case "/reportes-venta":
      return <TMCSalePage type="reportList" />;
    case "/configuraciones":
      return <TMCSalePage type="config" />;
    case "/recepciones-tmc":
      return <TMCSalePage type="stockTransfers" />;
    case "/cargar-listados":
      return <TMCSalePage type="loadList" />;
    case "/ver-listados":
      return <TMCSalePage type="viewList" />;
    case "/mis-ventas":
      return <TMCSalePage type="mySales" />;
    case "/bloqueados-app":
      return <StatusesPage type="app" />;
    case "/solicitudes-limites":
      return <SolicitudesLimitesPage />;
    case "/estadisticas-limites":
      return <EstadisticasLimitesPage />;
    case "/solicitud-compra-tmc":
      return <TmcSolicitudes mode="mine" />;
    case "/comercial/mis-tramites-capresca":
    case "/comercial/nueva-solicitud-capresca":
      return <CaprescaOperadorPage />;
    case "/bitacora-incidentes-sistemas":
      return <SystemsIncidentLogPage />;
    // Sistemas → Tenants API
    case "/sistemas/tenants":
      return <TenantsApiPage view="tenants" />;
    case "/sistemas/tenants-permisos":
      return <TenantsApiPage view="permisos" />;
    case "/sistemas/tenants-auditoria":
      return <TenantsApiPage view="auditoria" />;
    default:
      break;
  }

  switch (name) {
    case "Home":
      return <HomePage />;
    case "Roles":
      return <RolesPage />;
    case "Usuarios":
      return <UserPage type="users" />;
    case "Mis Licencias":
      return <LicencePage type="licences" />;
    case "Mis Vacaciones":
      return <LicencePage type="vacations" />;
    case "Licencias":
      return <LicencePage type="complete-licences" />;
    case "Vacaciones":
      return <LicencePage type="complete-vacations" />;
    case "Mi Perfil":
      return <ProfilePage />;
    case "Inventario Asignado":
      return <AssignedInventoryPage />;
    case "Inventario Stock":
      return <StockInventoryPage />;
    case "Colaboradores":
      return <UserPage type="collaborators" />;
    case "Visita a Sucursal":
      return <OrganizationMethodsPage />;
    case "Información sobre Licencias":
      return <LicenceInfoPage />;
    case "Solicitud de Soporte":
      return <TicketPage />;
    case "Modificar Estado Validacion":
      return <ValidationStatusPage />;
    case "Planilla Diaria":
      return <PowerbiPage name="daily" />;
    case "Encargados":
      return <PowerbiPage name="managers" />;
    case "Comercios":
      return <PowerbiPage name="shops" />;
    case "Comercios Activos":
      return <PowerbiPage name="activeShops" />;
    case "Analisis de Limites":
      return <PowerbiPage name="limitAnalysis" />;
    case "Administrar Dashboards":
      return <DashboardManagerPage />;
    case "Listado de Solicitudes":
      return <TicketPage type="list" />;
    case "Manuales":
      return <ManualsPage />;
    case "Administrar Manuales":
      return <AdministrarManualesPage />;
    case "Directorio Telefónico":
      return <DirectoryPage />;
    case "Telefonia IP":
      return <AsteriskPage type="sipUsers" />;
    case "Telefonia IP Llamadas":
      return <AsteriskPage type="cdr" />;
    case "Auditorias":
      return <AuditsPage />;
    case "Gestión de Recibos":
      return <ReceiptPage key="receipt-rrhh" type="rrhh" />;
    case "Mi Recibo":
      return <ReceiptPage key="receipt-user" type="user" />;
    case "App Mobile":
      return <AppMobilePage />;
    case "Marketing":
      return <MarketingPage />;
    case "Ingresar Venta por Banka":
      return <TMCSalePage key="sale-banka" type="sale" theme="green" />;
    case "Venta Producto de Stock":
      return <TMCSalePage key="sale-product" type="sale" theme="blue" />;
    case "Reportes de Venta":
      return <TMCSalePage type="reportList" />;
    case "Autorizaciones Excedido":
      return <TMCSalePage type="limitRequests" />;
    case "Configuraciones":
      return <TMCSalePage type="config" />;
    case "Cupones TMC":
      return <TMCSalePage type="coupons" />;
    case "Cargar Listados":
      return <TMCSalePage type="loadList" />;
    case "Stock TMC":
      return <TMCSalePage type="viewList" />;
    case "Mis Ventas":
      return <TMCSalePage type="mySales" />;
    case "Estado Solicitud":
      return <StatusesPage type="request" />;
    case "Solicitud Adicional":
      return <StatusesPage type="additional" />;
    case "Cambio G. Mora":
    case "Cambio G Mora":
      return <StatusesPage type="mora" />;
    case "Envío de Tarjeta":
    case "Envio de Tarjeta":
      return <StatusesPage type="card-shipping" />;
    case "Bloqueados App":
      return <StatusesPage type="app" />;
    case "Pago On-Line":
      return <OnlinePaymentPage />;
    case "Reimpresión de Ticket":
      return <PaymentReceiptReportPage />;
    case "Reporte de Pago":
      return <PaymentReportPage />;
    case "Conciliación Pagos Prospectos":
      return <PaymentReconciliationPage />;
    case "Resumen":
      return <ResumenPage />;
    case "Convertidor BNA":
      return <BnaPage />;
    case "BIND":
      return <BindPage />;
    case "Comercio QR":
      return <ComerceQr />;
    case "Respuestas Automáticas":
    case "Respuestas Automaticas":
      return <RespuestasAutomaticasPage />;
    case "Actualizar Prestamos":
      return <UpdateLoansPage />;
    case "Adelantar Cancelación de Oxígeno":
      return <OxygenCancellationPage />;
    case "Solicitudes TMC":
      return <TmcSolicitudes mode="manage" />;
    case "Retiro de Equipo":
      return <Withdrawal />;
    case "Gestión Prospectos":
      return <ProspectsPage type="gestion" />;
    case "Admin Prospectos":
      return <AdminProspectosPage />;
    case "Gestion de Altas":
      return <GestionAltasSocioPage />;
    case "Gestion de Prestamos":
      return <GestionPrestamosPage />;
    case "Agenda Automática":
      return <AdminProspectosPage initialComponent="agenda-automatica" />;
    case "Admin Prospectos - Dashboard":
      return <AdminProspectosPage initialComponent="Dashboard" />;
    case "Admin Prospectos - Reportes":
      return <AdminProspectosPage initialComponent="reportes" />;
    case "Admin Prospectos - Speech":
      return <AdminProspectosPage initialComponent="speech" />;
    case "Admin Prospectos - Campañas":
      return <AdminProspectosPage initialComponent="campanas" />;
    case "Admin Prospectos - Areas y Colaboradores":
      return <AdminProspectosPage initialComponent="areas-colaboradores" />;
    case "Admin Prospectos - Opciones de Respuesta":
      return <AdminProspectosPage initialComponent="opciones-respuesta" />;
    case "Admin Prospectos - Asignar Opciones":
      return <AdminProspectosPage initialComponent="asignar-opciones" />;
    case "Admin Prospectos - Agenda Automática":
      return <AdminProspectosPage initialComponent="agenda-automatica" />;
    case "Admin Prospectos - Cargar Base":
      return <AdminProspectosPage initialComponent="cargar-base" />;
    case "Admin Prospectos - Carga Masiva Gestiones":
      return <AdminProspectosPage initialComponent="carga-masiva-gestiones" />;
    case "Admin Prospectos - Asignar Prospectos":
      return (
        <AdminProspectosPage initialComponent="asignar-desasignar-prospectos" />
      );
    case "Turnos Kiosco":
    case "Kiosco":
      return <TurnosPage />;
    case "Turnos Display":
    case "Display":
      return <TurnosPage />;
    case "Turnos Operador":
    case "Operador":
      return <TurnosPage />;
    case "Turnos Admin":
    case "Administración":
      return <TurnosPage />;
    case "Banners":
      return <BannersPage />;
    case "Eventos Sistema":
    case "Eventos del Sistema":
      return <EventosSistemaPage />;
    case "Bitácora de Incidentes":
    case "Bitacora de Incidentes":
      return <SystemsIncidentLogPage />;
    case "Partición de Lotes":
    case "Lotes":
      return <LotesPage />;
    case "Tasas y Comisiones":
      return <TasasComisionesPage />;
    case "Subir PDF Tasas":
      return <SubirTasasPDFPage />;
    case "Comercios Primera Venta":
      return <ComerciosPrimeraVentaPage />;
    case "Campañas App Clientes":
    case "Campanias App Clientes":
    case "Campañas":
    case "Campanias":
      return <CampaniasPage />;
    case "Mis Trámites":
    case "Mis Tramites":
    case "Nueva Solicitud":
      return <CaprescaOperadorPage />;
    case "Gestión Empleados Públicos":
    case "Gestion Empleados Publicos":
      return <CaprescaPage />;
    case "Ingresos Empleados Públicos":
      return <IngresosEmpleadosPublicosPage />;
    case "Calculadora Empleados Públicos":
      return <CalculadoraEmpleadosPublicosPage />;
    case "Administración de Solicitudes de Límites":
    case "Administracion de Solicitudes de Limites":
    case "Solicitudes Capresca":
    case "Solicitudes de Límites":
    case "Solicitudes de Limites":
      return <SolicitudesLimitesPage />;
    case "Estadísticas de Limites":
    case "Estadisticas de Limites":
    case "Estadísticas Capresca":
      return <EstadisticasLimitesPage />;

    case "Parametros Adelantos":
    case "Parámetros Adelantos":
      return <AdelantosEfectivoPage />;

    case "Parametros Prestamos":
    case "Parámetros Préstamos":
    case "Prestamos Parametrizados":
    case "Préstamos Parametrizados":
      return <PrestamosParametrizadosPage />;

    default:
      return null;
  }
};

export default DynamicRoutes;
